import React from 'react';
import {translateService} from "../../services/TranslateService";
import {resetUrlForResCancelled} from "../../GetUrlParams";
import {useClarityStep} from "../../services/clarity/useClarityStep";
import SuccessProgressSteps from "./SuccessProgressSteps";
import {formatTemplate} from "../../GetUrlParams";
import {LoadingSpinner} from "../BusyModal/BusyModal";
import ApiService from "../../services/api/ApiService";
import {useDispatch} from "react-redux";
import { parseRidSession } from "../../interfaces/RidSessionData";

interface PaymentSuccessProps {
    rootUrl: string,
    returnToHome: Function;
    changeVirtualPageInfo: Function;
    mobileDevice: boolean | undefined,
    apiService: ApiService | undefined;
    changeResCancelled: Function;
}

function getStartParts(raw?: string | null): [string | null, string | null] {
    if (!raw) return [null, null];
    const [first, ...rest] = raw.split('@');
    const clean = (s?: string) => {
        if (s == null) return null;
        const out = s.replace(/\s+/g, '');
        return out === '' ? null : out;
    };
    return [clean(first), clean(rest.join('@'))];
}

export default function PaymentSuccess ({rootUrl, returnToHome, changeVirtualPageInfo, mobileDevice, apiService, changeResCancelled} : PaymentSuccessProps ) {
    const parsedData = parseRidSession(sessionStorage.getItem('rid-1'));
    const launcherMode = sessionStorage.getItem('redirectRef') === 'launcher' ? '1' : undefined;
    const [ sDate, sTime ] = getStartParts(parsedData?.startDate);
    const [loading, setLoading] = React.useState(true);
    const dispatch = useDispatch();

    const exitToLauncherMyReservations = () => {
        window.location.href = `rentapp://launch-screen/MyReservations?lang=${parsedData?.lang === 'en' ? 'en' : 'fi'}`;
    };

    useClarityStep('resultSuccess', {
        city: parsedData?.pickupLoc || '',
        start: parsedData?.startDate || '',
        end: parsedData?.endDate || '',
        loggedIn: parsedData?.li ?? false,
        paymentMethod: parsedData?.paymentMethod,
        resId: String(parsedData?.resId ?? ''),
        oneway: parsedData?.returnLoc ? true : undefined,
    });

    React.useEffect(() => {
        if (parsedData) return;
        const loadingTimeout = setTimeout(() => setLoading(false), 5000);
        const redirectTimeout = setTimeout(() => {
            // Redirect logic --> TBD for redesigned pages --> can be different according to bts + app mode
            returnToHome();
        }, 3000);

        return () => {
            clearTimeout(loadingTimeout);
            clearTimeout(redirectTimeout);
        };
    }, [parsedData]);

    React.useEffect(() => {
        let scrollPoint = document.getElementById("paymentSuccessScrollPoint");
        if (scrollPoint) {
            scrollPoint.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        changeVirtualPageInfo('paymentSuccess');

        sessionStorage.setItem('paymentSuccess', '1'); // for browsers back button detection (clears data to avoid multiple payments)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const cancelReservation = () => {
        // cancelling res is redirected to different 24rent handling page
        dispatch({ // show loading
            type: 'SET_API_MESSAGE',
            payload: translateService.translate('paymentCallbackScreens.cancellingRes'),
        });

        apiService?.reservation.cancelReservation(parsedData?.resId ?? -1).then((cancelRes) => {
            if (cancelRes?.cancelled === true) {
                resetUrlForResCancelled();
                changeResCancelled(true);
            }

        }).catch((err: unknown) => {
            dispatch({
                type: 'SET_RESPONSE_MODAL',
                payload: err instanceof Error ? err.message : translateService.translate('paymentCallbackScreens.cancelResFailed'),
            });
        }).finally(() => {
            dispatch({ // show loading
                type: 'SET_API_MESSAGE',
                payload: '',
            });
        })
    }

    if (!parsedData) {
        if (loading) {
            return (<LoadingSpinner />)
        }
        return (
             <div className='callback-info-container' id={"paymentSuccessScrollPoint"}>
                <img width="104" height="30" src={rootUrl+'/assets/logos/24rent-logo-dark.svg'} alt="24Rent logo" loading="lazy"/>
                <h1 className='payment-callback-heading'> {translateService.translate('paymentCallbackScreens.errorRetrievingResData')} </h1>
                <br/>
                <div className='callback-info-section'>
                    <button className="btn btn-secondary btn-outline-secondary btn-cta mt-4 w-100"
                            onClick={() => {returnToHome()}}>
                        {translateService.translate('vehicleSearch.noVehicleSelectedButton')}
                    </button>
                </div>
            </div>
        )
    }

    const ManageResLink = () => {
        return (
            <>
                <div className={`logo-heading-row ${mobileDevice ? 'mt-4' : 'mt-3'}`}>
                    <img width="25" height="25" src={rootUrl + '/assets/icons/settings-icon.png'} alt="24Rent logo" loading="lazy" />
                    <h2 className='payment-callback-heading text-center'>
                        {translateService.translate('paymentCallbackScreens.thanksHeading3')}
                    </h2>
                </div>
                <span className='text-center'>{translateService.translate('paymentCallbackScreens.thanksCanUseChauffeur')} <a href={"https://m.24rent.fi"}>https://m.24rent.fi </a></span>
            </>
        )
    }

    if (!launcherMode) { // non-launcher & logged in user view
        const mobileDownload = 'https://www.24rent.fi/appstorelinks/';
        const AppDownload = () => {
            return (
                mobileDevice ?
                        <div className='d-flex justify-content-center'>
                            <button className="btn btn-primary btn-outline-primary btn-cta mt-2"
                                    onClick={() => window.location.href = 'https://download.24rent.fi'}>
                                {translateService.translate('paymentCallbackScreens.thanksBtn1')}
                            </button>
                        </div>
                        : <div className="appStoreButtons mt-2">
                        <img width="130" className='left-app-img'
                             onClick={() => window.location.href = !mobileDevice ?
                                 'https://play.google.com/store/apps/details?id=fi.twentyfourrent.app&output=embed&pli=1' : mobileDownload}
                             src={rootUrl + '/assets/icons/google-play.svg'} alt="" loading="lazy" />
                        <img width="130"
                             onClick={() => window.location.href = !mobileDevice ?
                                 'https://apps.apple.com/us/app/24rent/id1070267611' : mobileDownload}
                             src={rootUrl + '/assets/icons/apple-store.svg'} alt="" loading="lazy" />
                    </div>
            )
        }

        if (parsedData?.li) {
            const sectionClass = `payment-callback-section py-3 ${mobileDevice ? 'mobile-top-line' : 'orange-border'}`;
            return (
                <div className='callback-info-container pb-3' id="paymentSuccessScrollPoint">
                    <div className={sectionClass}>
                        <div className="logo-heading-row">
                            {!mobileDevice && (
                                <img width="35" height="35" src={rootUrl + '/assets/icons/round-checkmark.png'} alt="24Rent logo" loading="lazy" />
                            )}
                            <h1 className='payment-callback-heading text-center'>
                                {translateService.translate('paymentCallbackScreens.thankYouForRes')}, {parsedData.firstName}!
                            </h1>
                        </div>
                        <span className='text-center'>
                        {translateService.translate('paymentCallbackScreens.thanksLoggedInPcP1text1')}
                            <b>{parsedData.resId}</b>
                            {formatTemplate(
                                translateService.translate('paymentCallbackScreens.thanksLoggedInPcP1text2'),
                                { pickupDate: sDate ?? '', pickupTime: sTime ?? '' })}
                    </span>
                        <h2 className='mt-3'>{translateService.translate('paymentCallbackScreens.thanksLoggedInPcHeading2')}</h2>
                        <div>
                            <span>{translateService.translate('paymentCallbackScreens.thanksLoggedInPcP2')}</span>
                            <ul className="thanks-bullets-list">
                                {[1, 2, 3, 4].map(i => (
                                    <li key={i}>
                                        <img width="18" height="18" src={rootUrl + '/assets/icons/square-checkmark.png'} alt="" loading="lazy" />
                                        {translateService.translate(`paymentCallbackScreens.thanksLoggedInBullet${i}`)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <AppDownload />
                        {mobileDevice && (<ManageResLink/>)}
                    </div>
                    {!mobileDevice && (<ManageResLink/>)}
                </div>
            );
        } else { // new customer: not logged in view
            const sectionClass = `payment-callback-section py-3 ${mobileDevice ? 'mobile-top-line' : 'orange-border'}`;
            const PhoneNumCheckSection = () => {
                return (
                    <>
                        <div className={`logo-heading-row ${mobileDevice ? 'mt-4' : 'mt-3'}`}>
                            <img width="25" height="25" src={rootUrl + '/assets/icons/profile-icon.png'} alt="24Rent logo" loading="lazy" />
                            <h2 className='payment-callback-heading'>
                                {translateService.translate('paymentCallbackScreens.thanksNewUserText5')}
                            </h2>
                        </div>
                        <span className='text-center mb-1'>{formatTemplate(
                            translateService.translate('paymentCallbackScreens.thanksNewUserPhoneCheckText6'),
                            { phoneNumber: parsedData.num ?? ''})}</span>
                        <button onClick={() => cancelReservation()} className='btn-as-link'>
                            {translateService.translate('paymentCallbackScreens.thanksNewUserLink1')}
                        </button>
                    </>
                )
            }

            const OtherWaysToManageResSection = () =>  {
                return (
                    <>
                        <div className={`logo-heading-row ${mobileDevice ? 'mt-4' : 'mt-3'}`}>
                            <img width="25" height="25" src={rootUrl + '/assets/icons/mobile-icon.png'} alt="24Rent logo" loading="lazy" />
                            <h2 className='payment-callback-heading text-center'>
                                {translateService.translate('paymentCallbackScreens.rent24App')}
                            </h2>
                        </div>
                        <span className='text-center'>{translateService.translate('paymentCallbackScreens.aboutApp')}</span>
                        <AppDownload />
                    </>
                )
            }

            return (
                <div className='callback-info-container pb-3' id="paymentSuccessScrollPoint">
                    <div className={sectionClass}>
                        <SuccessProgressSteps labels={[translateService.translate('paymentCallbackScreens.progressBar1'),
                            translateService.translate('paymentCallbackScreens.progressBar2'), translateService.translate('paymentCallbackScreens.progressBar3')]}/>
                        <div className="logo-heading-row">
                            {!mobileDevice && (<img width="35" height="35" src={rootUrl + '/assets/icons/round-checkmark.png'} alt="24Rent logo" loading="lazy" />)}
                            <h1 className='payment-callback-heading text-center'>
                                {translateService.translate('paymentCallbackScreens.thankYouForRes')}!
                            </h1>
                        </div>
                        <span className='text-center px-2'>{formatTemplate(
                                translateService.translate('paymentCallbackScreens.thanksNewUserText1'),
                                { resId: String(parsedData.resId ?? ''), pickupDate: sDate ?? '', pickupTime: sTime ?? '' })}
                        </span>
                        <div style={{width: !mobileDevice ? '50%' : '', textAlign: 'center'}}>
                            <h2 className='mt-4 mb-2'>{translateService.translate('paymentCallbackScreens.thanksNewUserText2')}</h2>
                            <span>{translateService.translate('paymentCallbackScreens.thanksNewUserText3')}</span>

                            <div className='d-flex justify-content-center mb-3 mt-2'>
                                <button className="btn btn-primary btn-outline-primary btn-cta mt-2"
                                        onClick={() => window.location.href = 'https://m.24rent.fi?lang=' + (parsedData?.lang === 'en' ? 'en' : 'fi')}>
                                    {translateService.translate('paymentCallbackScreens.startRes')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={`payment-callback-section ${mobileDevice ? 'mobile-extra-content' : 'split-space-50'}`}>
                        {mobileDevice ?
                            <>
                                <PhoneNumCheckSection />
                                <OtherWaysToManageResSection />
                            </> :
                            <>
                                <div className={mobileDevice ? ' mobile-center' : 'divided-block'}>
                                    <PhoneNumCheckSection />
                                </div>
                                <div className='divided-block'>
                                    <OtherWaysToManageResSection />
                                </div>
                            </>}
                    </div>
                </div>
            )
        }
    } else { // LAUNCHER CUSTOMERS VIEW
        if (parsedData?.li) {
            const sectionClass = `payment-callback-section py-3 mobile-top-line`;
            return (
                <div className='callback-info-container pb-3' id="paymentSuccessScrollPoint">
                    <div className={sectionClass}>
                        <SuccessProgressSteps labels={[translateService.translate('paymentCallbackScreens.progressBar1'),
                            translateService.translate('paymentCallbackScreens.progressBar2'), translateService.translate('paymentCallbackScreens.progressBar3')]}/>
                        <div className="logo-heading-row">
                            <h1 className='payment-callback-heading text-center'>
                                {translateService.translate('paymentCallbackScreens.thankYouForRes')}, {parsedData.firstName}!
                            </h1>
                        </div>
                        <span className='text-center mb-3'>
                        {translateService.translate('paymentCallbackScreens.thanksLoggedInPcP1text1')}
                            <b>{parsedData.resId}</b>
                            {formatTemplate(
                                translateService.translate('paymentCallbackScreens.thanksLoggedInPcP1text2'),
                                { pickupDate: sDate ?? '', pickupTime: sTime ?? '' })}
                        </span>
                        <div className='d-flex justify-content-center mb-3'>
                            <button className="btn btn-primary btn-outline-primary btn-cta mt-2"
                                    onClick={exitToLauncherMyReservations}>
                                {translateService.translate('paymentCallbackScreens.launcherViewYourRes')}
                            </button>
                        </div>
                        { !parsedData.b2b ?
                            <>
                                <h2 className='mt-3'>{translateService.translate('paymentCallbackScreens.launcherPointsHeading')}</h2>
                                <span className='text-center w-50 mb-3'>
                                    {translateService.translate('paymentCallbackScreens.launcherPointsText1')}
                                </span>
                                {/*<span className='text-center w-50'>*/}
                                {/*    {translateService.translate('paymentCallbackScreens.launcherPointsText2')}*/}
                                {/*</span>*/}
                                {/*<div className='logo-heading-row mt-2'>*/}
                                {/*    <img width="35" height="35" src={rootUrl + '/assets/icons/trophy-icon.png'} alt="24Rent logo" loading="lazy" />*/}
                                {/*    <h1 className='r24Points-amount'> {translateService.translate('paymentCallbackScreens.launcherPointsAmount')} </h1>*/}
                                {/*</div>*/}
                                <a href={parsedData?.lang === 'en' ? "https://www.24rent.fi/en/rewards-loyalty/" : "https://www.24rent.fi/rewards/"}>
                                    {translateService.translate('paymentCallbackScreens.launcherPointsLink')}
                                </a>
                            </> : null }
                        </div>
                </div>
            );

        } else { // launcher new customer view
            const sectionClass = `payment-callback-section py-3 mobile-top-line`;
            return (
                <div className='callback-info-container pb-3' id="paymentSuccessScrollPoint">
                    <div className={sectionClass}>
                        <div className="logo-heading-row">
                            <h1 className='payment-callback-heading text-center'>
                                {translateService.translate('paymentCallbackScreens.thankYouForRes')}!
                            </h1>
                        </div>
                        <span className='text-center mb-3'>
                            {formatTemplate(
                                translateService.translate('paymentCallbackScreens.launcherNewUserText1'),
                                { resId: String(parsedData?.resId ?? ''),pickupDate: sDate ?? '', pickupTime: sTime ?? '' })}
                        </span>

                        <div className='logo-heading-row mt-2'>
                            <img width="20" height="20" src={rootUrl + '/assets/icons/profile-icon.png'} alt="24Rent logo" loading="lazy" />
                            <h2 className='payment-callback-heading text-center'>
                                {translateService.translate('paymentCallbackScreens.launcherNewUserText2')}
                            </h2>
                        </div>
                        <span className={'w-75 text-center mb-2'}>
                            {translateService.translate('paymentCallbackScreens.launcherNewUserText3')}
                        </span>

                        <div className='d-flex justify-content-center mb-3'>
                            <button className="btn btn-primary btn-outline-primary btn-cta mt-2"
                                    onClick={exitToLauncherMyReservations}>
                                {translateService.translate('paymentCallbackScreens.thanksNewUserBtn1')}
                            </button>
                        </div>
                        <span className={'w-75 text-center mb-2'}>
                             {translateService.translate('paymentCallbackScreens.launcherNewUserText4')}
                        </span>
                        { !parsedData?.b2b ?
                            <>
                                <h2 className='mt-3'>{translateService.translate('paymentCallbackScreens.launcherPointsHeading')}</h2>
                                <span className='text-center w-50 mb-3'>
                                    {translateService.translate('paymentCallbackScreens.launcherPointsText1')}
                                </span>
                                {/*<span className='text-center w-50'>*/}
                                {/*    {translateService.translate('paymentCallbackScreens.launcherPointsText2')}*/}
                                {/*</span>*/}
                                {/*<div className='logo-heading-row mt-2'>*/}
                                {/*    <img width="35" height="35" src={rootUrl + '/assets/icons/trophy-icon.png'} alt="24Rent logo" loading="lazy" />*/}
                                {/*    <h1 className='r24Points-amount'> {translateService.translate('paymentCallbackScreens.launcherPointsAmount')} </h1>*/}
                                {/*</div>*/}
                                <a href={parsedData?.lang === 'en' ? "https://www.24rent.fi/en/rewards-loyalty/" : "https://www.24rent.fi/rewards/"}>
                                    {translateService.translate('paymentCallbackScreens.launcherPointsLink')}
                                </a>
                            </> : null }
                    </div>
                </div>
            );

        }
    }
}